/**
 * Shared responsive breakpoints — the CSS-side single source of truth
 * (2026-08-03 desktop-polish pass). Values mirror src/hooks/useResponsive.ts
 * exactly; keep the two in lockstep or styled-components and JS hooks will
 * disagree about which breakpoint is active.
 *
 * Usage (mobile-first, min-width only):
 *   const Card = styled.div`
 *     padding: 1.25rem;
 *     ${media.tablet} { padding: 1.5rem; }
 *     ${media.laptop} { padding: 1.75rem; }
 *   `;
 */

export const BREAKPOINTS = {
  tablet: 768,
  laptop: 1024,
  desktop: 1200,
} as const;

/**
 * THE PHONE BREAKPOINT — one number, in one place (2026-09-13).
 *
 * "Mobile" had three unrelated definitions. The chat decided it with a bare
 * `matchMedia('(max-width: 820px)')` written out twice in useGlobalChat.ts;
 * installState.ts declared the same string again as "the chat's phone
 * breakpoint, as a fact" and the chat did not import it; GlobalFooter,
 * DemoVideoModal and LandingPage each carried their own copy; and the
 * BREAKPOINTS above say 768.
 *
 * The composer was the case that mattered: its CSS tiers gated at **720** while
 * the state that puts the chat into its phone layout gates at **820**, so
 * 721-820px got the fullscreen takeover, no dock and the compact header rail —
 * and the desktop composer. Measured 2026-09-13 at 820x520: the phone tiers
 * never fired. That is a dead zone, not a rounding difference.
 *
 * It is DELIBERATELY not folded into BREAKPOINTS above. Those three are the
 * app's mobile-first min-width scale; this is a max-width question about one
 * specific thing — whether the chat wears its phone layout — and the two
 * answers are 768 and 820 for good reasons. Naming it separately is what stops
 * the next reader from "unifying" them and moving the takeover 52px.
 */
export const MOBILE_VIEWPORT_MAX_PX = 820;
export const MOBILE_VIEWPORT_QUERY = `(max-width: ${MOBILE_VIEWPORT_MAX_PX}px)`;

/**
 * Max-width queries, for the places that genuinely key off "this is a phone"
 * rather than "this is at least a tablet". Sparse on purpose: the rest of the
 * tier zoo in GlobalChatSystem/styles.ts (900, 700, 640, 600, 520, 480, 420,
 * 360) is tuned per component and per screenshot, and collapsing it would be an
 * unverifiable renumber whose failure mode is silent visual drift.
 */
export const mediaMax = {
  mobileViewport: `@media ${MOBILE_VIEWPORT_QUERY}`,
  tablet: `@media (max-width: ${BREAKPOINTS.tablet - 1}px)`,
} as const;

export const media = {
  tablet: `@media (min-width: ${BREAKPOINTS.tablet}px)`,
  laptop: `@media (min-width: ${BREAKPOINTS.laptop}px)`,
  desktop: `@media (min-width: ${BREAKPOINTS.desktop}px)`,
} as const;
